'use client';

/**
 * API namespace `giftCards` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L7345-L7570).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const giftCards = {
  /**
   * GET /api/seller/gift-cards?page=&limit=&status=&includeInactive= —
   * list the seller's own gift cards (paginated, 1-indexed). Returns
   * `GiftCardListResponse` (`giftCards` + total + page + limit) —
   * each gift card is the EXPANDED snapshot (`GiftCardResponse` with
   * the seller's storeName + storeSlug snapshot).
   *
   * Authz: self only.
   *
   * Spec ref: SPEC-242 §3.5 (GET /seller/gift-cards), §3.6.2
   *           (catálogo público UI).
   */
  async list(
    page = 1,
    limit = 20,
    options?: { status?: GiftCardStatus; includeInactive?: boolean },
    signal?: AbortSignal,
  ): Promise<GiftCardListResponse> {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (options?.status) params.set('status', options.status);
    if (options?.includeInactive !== undefined) {
      params.set('includeInactive', String(options.includeInactive));
    }
    return apiFetch<GiftCardListResponse>(
      `/seller/gift-cards?${params.toString()}`,
      { signal },
    );
  },
  /**
   * GET /api/seller/gift-cards/:giftCardId — seller's own view of a
   * single gift card. Returns `GiftCard` (the entity).
   *
   * Authz: self only.
   *
   * Spec ref: SPEC-242 §3.5 (GET /seller/gift-cards/:giftCardId).
   */
  async get(
    giftCardId: string,
    signal?: AbortSignal,
  ): Promise<GiftCard> {
    return apiFetch<GiftCard>(
      `/seller/gift-cards/${encodeURIComponent(giftCardId)}`,
      { signal },
    );
  },
  /**
   * POST /api/seller/gift-cards — create a new gift card.
   *
   * Server defaults: `giftCardId=UUID`, `status='pending_approval'`
   * (INV-MKT-20), `active=true`.
   *
   * The Zod `giftCardCreateSchema` cross-field rules
   * (customDenomination=true → min+max required + min ≤ max,
   * redemptionUrl required when redemptionMethod='website',
   * redemptionContact required when redemptionMethod in {contact,
   * whatsapp, phone, email}) are enforced server-side.
   *
   * Authz: `requireAuth` + `requireActiveSeller`.
   *
   * Spec ref: SPEC-242 §3.3.3 (GiftCard model), §3.4.2 (reglas de
   *           negocio), §3.5 (POST /seller/gift-cards endpoint).
   */
  async create(
    data: GiftCardCreateInput,
    signal?: AbortSignal,
  ): Promise<GiftCard> {
    return apiFetch<GiftCard>('/seller/gift-cards', {
      method: 'POST',
      body: JSON.stringify(data),
      signal,
    });
  },
  /**
   * PUT /api/seller/gift-cards/:giftCardId — partial update.
   *
   * Editing a gift card is only allowed when `status='pending
   * _approval'` OR `status='paused'` (same UX rule as Coupon +
   * ProductBundle per SPEC-242 §3.5 + §4.3). Enforced in the service
   * layer.
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only.
   *
   * Spec ref: SPEC-242 §3.5 (PUT /seller/gift-cards/:giftCardId).
   */
  async update(
    giftCardId: string,
    data: GiftCardUpdateInput,
    signal?: AbortSignal,
  ): Promise<GiftCard> {
    return apiFetch<GiftCard>(
      `/seller/gift-cards/${encodeURIComponent(giftCardId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        signal,
      },
    );
  },
  /**
   * DELETE /api/seller/gift-cards/:giftCardId — soft delete.
   *
   * Sets `active=false` (SPEC-242 §3.3.3 — NOT a 'deleted' status
   * value). Idempotent.
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only +
   * `requireRecent2FA` (INV-SEC-06 §9.6 — creates AuditLog
   * 'GIFT_CARD_DELETED').
   *
   * Spec ref: SPEC-242 §3.5 (DELETE /seller/gift-cards/:giftCardId),
   *           §3.3.3 (`active` flag — soft delete);
   *           SPEC-NEGOCIO §9.6 (INV-SEC-06 — 2FA for sensitive ops).
   */
  async delete(
    giftCardId: string,
    signal?: AbortSignal,
  ): Promise<{ giftCardId: string; active: false }> {
    return apiFetch<{ giftCardId: string; active: false }>(
      `/seller/gift-cards/${encodeURIComponent(giftCardId)}`,
      { method: 'DELETE', signal },
    );
  },
  /**
   * GET /api/seller/gift-cards/:giftCardId/stats — minimal summary
   * view of a gift card's denomination breakdown + status +
   * timestamps. Returns `GiftCardStats` (locally defined above).
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only.
   *
   * Spec ref: SPEC-242 §3.5 (GET /seller/gift-cards/:giftCardId/stats).
   */
  async stats(
    giftCardId: string,
    signal?: AbortSignal,
  ): Promise<GiftCardStats> {
    return apiFetch<GiftCardStats>(
      `/seller/gift-cards/${encodeURIComponent(giftCardId)}/stats`,
      { signal },
    );
  },
  /**
   * POST /api/seller/gift-cards/validate — validate a gift card
   * redemption code (SPEC-242 §3.4.3). The seller enters the
   * `redemptionCode` (format `GIFT-XXXXXXXX`); the backend marks the
   * `GiftCardPurchase` as 'redeemed' + returns the
   * `GiftCardPurchaseResponse` (with snapshot fields the seller panel
   * renders: giftCardTitle, giftCardBrandName, denominationCredits,
   * recipientName + recipientEmail for identity verification, etc.).
   *
   * Same authz + 2FA conventions as `coupons.validate` above — NO 2FA
   * required (state transition on the purchase record, NOT a
   * credit-debit operation).
   *
   * Spec ref: SPEC-242 §3.4.3 (validación de redención), §3.5
   *           (POST /seller/gift-cards/validate endpoint), §3.6.3
   *           (seller UI — validar código de redención).
   */
  async validate(
    redemptionCode: string,
    signal?: AbortSignal,
  ): Promise<GiftCardPurchaseResponse> {
    return apiFetch<GiftCardPurchaseResponse>('/seller/gift-cards/validate', {
      method: 'POST',
      body: JSON.stringify({ redemptionCode }),
      signal,
    });
  },
  /**
   * Convenience constant — the 4 valid `GiftCardStatus` values for
   * the seller dashboard status filter dropdown.
   *
   * 4 values (NOT 5/6) — `GiftCardStatus` does NOT include 'deleted'
   * (gift cards use the `active: boolean` flag for soft delete per
   * SPEC-242 §3.3.3) NOR 'out_of_stock' (gift cards are digital
   * codes, no stock) NOR 'expired' (no countdown timer on the
   * GiftCard entity itself — the per-purchase `GiftCardPurchase.
   * expiresAt` is the canonical expiration marker per SPEC-242 §3.4.2).
   *
   * §6 Anti-Invención: the values mirror `GiftCardStatus` verbatim
   * (Track C #9 — `types/coupon.ts`) — NOT invented here.
   *
   * Spec ref: SPEC-242 §3.3.3 (GiftCardStatus enum), §3.6.3 (seller
   *           UI list table — status filter dropdown).
   */
  STATUS_VALUES: [
    'pending_approval',
    'approved',
    'rejected',
    'paused',
  ] as ReadonlyArray<GiftCardStatus>,
};
